import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Send, CheckCircle2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORMSPREE_URL = "https://formspree.io/f/xojgokyn";
const SERVICES_OPTIONS = [
  "Traitement antirouille",
  "Polissage & protection céramique",
  "Cire de protection haute performance",
  "Nettoyage complet intérieur & extérieur",
  "Autre / à discuter",
];

const initialForm = { nom: "", telephone: "", courriel: "", vehicule: "", service: SERVICES_OPTIONS[0], message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, courriel: form.courriel || null, vehicule: form.vehicule || null, message: form.message || null };
      const formspreePayload = {
        nom: form.nom,
        telephone: form.telephone,
        courriel: form.courriel || "",
        _replyto: form.courriel || "",
        vehicule: form.vehicule || "",
        service: form.service,
        message: form.message || "",
        _subject: `Nouvelle soumission — ${form.nom} (${form.service})`,
      };
      const [formspreeResult] = await Promise.allSettled([
        axios.post(FORMSPREE_URL, formspreePayload, { headers: { Accept: "application/json" } }),
        axios.post(`${API}/soumissions`, payload),
      ]);
      if (formspreeResult.status !== "fulfilled") {
        throw new Error("Formspree a refusé la demande");
      }
      setSent(true);
      setForm(initialForm);
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "formulaire",
          event_label: form.service,
        });
      }
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
    } catch (err) {
      toast.error("Une erreur est survenue. Appelez-nous au 514-269-0261.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 lg:py-36 relative overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-96 w-[700px] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Soumission gratuite</Eyebrow>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl mb-6">
              Parlons de <span className="gold-gradient-text">votre véhicule</span>
            </h2>
            <p className="text-base text-white/65 font-light leading-relaxed mb-10">
              Décrivez votre besoin en quelques lignes et recevez une soumission personnalisée,
              sans engagement. Réponse rapide, généralement le jour même.
            </p>
            <div className="space-y-5">
              <a
                data-testid="contact-phone-link"
                href="tel:5142690261"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold transition-transform duration-300 group-hover:scale-110">
                  <Phone size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-white/45">Téléphone</span>
                  <span className="text-lg font-medium text-white group-hover:text-gold transition-colors duration-300">514-269-0261</span>
                </span>
              </a>
              <a
                data-testid="contact-email-link"
                href="mailto:premium.autoshine.mtl@gmail.com"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold transition-transform duration-300 group-hover:scale-110">
                  <Mail size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-white/45">Courriel</span>
                  <span className="text-sm sm:text-base font-medium text-white group-hover:text-gold transition-colors duration-300 break-all">premium.autoshine.mtl@gmail.com</span>
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/60">
                  <MapPin size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-white/45">Zone desservie</span>
                  <span className="text-sm text-white/80">Drummondville, Québec et les environs</span>
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/60">
                  <Clock size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-white/45">Disponibilité</span>
                  <span className="text-sm text-white/80">Lundi au samedi, sur rendez-vous</span>
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="glass-gold rounded-2xl p-8 lg:p-10 relative">
              {sent ? (
                <motion.div
                  data-testid="quote-success-message"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <CheckCircle2 size={56} className="mx-auto text-gold mb-6" strokeWidth={1.4} />
                  <h3 className="font-display font-bold text-2xl mb-3">Demande bien reçue</h3>
                  <p className="text-sm text-white/60 font-light max-w-md mx-auto mb-8">
                    Merci pour votre confiance. Un membre de notre équipe vous contactera très bientôt
                    pour finaliser votre soumission.
                  </p>
                  <button
                    data-testid="quote-new-request-button"
                    onClick={() => setSent(false)}
                    className="rounded-full border border-gold/40 px-6 py-3 text-xs uppercase tracking-[0.25em] text-gold hover:bg-gold/10 transition-colors duration-300"
                  >
                    Faire une autre demande
                  </button>
                </motion.div>
              ) : (
                <form data-testid="quote-form" onSubmit={submit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="nom" className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-2">Nom complet *</label>
                      <input id="nom" data-testid="quote-input-nom" required value={form.nom} onChange={update("nom")} className="field-input" placeholder="Votre nom" />
                    </div>
                    <div>
                      <label htmlFor="telephone" className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-2">Téléphone *</label>
                      <input id="telephone" data-testid="quote-input-telephone" required type="tel" value={form.telephone} onChange={update("telephone")} className="field-input" placeholder="514-000-0000" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="courriel" className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-2">Courriel</label>
                      <input id="courriel" data-testid="quote-input-courriel" type="email" value={form.courriel} onChange={update("courriel")} className="field-input" placeholder="vous@exemple.com" />
                    </div>
                    <div>
                      <label htmlFor="vehicule" className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-2">Véhicule</label>
                      <input id="vehicule" data-testid="quote-input-vehicule" value={form.vehicule} onChange={update("vehicule")} className="field-input" placeholder="Ex : BMW X5 2022" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="service" className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-2">Service souhaité *</label>
                    <select id="service" data-testid="quote-select-service" value={form.service} onChange={update("service")} className="field-input appearance-none cursor-pointer">
                      {SERVICES_OPTIONS.map((s) => (
                        <option key={s} value={s} className="bg-[#0a0a0a] text-white">{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-2">Message</label>
                    <textarea id="message" data-testid="quote-textarea-message" rows={4} value={form.message} onChange={update("message")} className="field-input resize-none" placeholder="Décrivez l'état de votre véhicule et vos attentes…" />
                  </div>
                  <button
                    data-testid="submit-quote-btn"
                    type="submit"
                    disabled={loading}
                    className="group relative w-full rounded-full bg-gold py-4 font-display font-bold uppercase tracking-widest text-sm text-black overflow-hidden transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                      {loading ? "Envoi en cours…" : "Envoyer ma demande de soumission"}
                    </span>
                    <span className="absolute inset-0 bg-gold-light translate-y-full transition-transform duration-400 ease-out group-hover:translate-y-0" />
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
